import {
  CardioOrResistance,
  IGymStateSessionViewModel,
  SessionSequenceEntry,
} from '../../entities';
import { DbGymRepository } from '../../persistence';
import { ScreenSession } from './screenSession';
import { TimeSlices } from './timeslices';
import { ZoneFallback } from './zone-fallback';

export interface IScheduleBuilderConfig {
  backPressureWindow: number;
  secondsDifferenceAllowedInSchedule: number;
  realtimeMetricsServer: string;
}

export interface IGymStateForSingleZone {
  [key: string]: {
    leftStationInZone: IGymStateSessionViewModel,
    rightStationInZone: IGymStateSessionViewModel,
    audioSequenceForAllZones: SessionSequenceEntry[],
    startSlice: Date,
    endSlice: Date,
  };
}

export interface IClientSideLogger {
  error(e: Error, customErrorMessage?: string): void;
  info(msg: string): void;
  warn(msg: string): void;
  trace(msg: string): void;
}

export class ScheduleBuilder {
  public static getUniqueUserIds(gymStateForSingleZone: IGymStateForSingleZone): string[] {
    // todo: add a test for this, with filter from a certain time
    const keys = Object.keys(gymStateForSingleZone);

    const userIds: string[] = keys.flatMap((key): string[] => {
      const left = gymStateForSingleZone[key].leftStationInZone.userProfile;
      const right = gymStateForSingleZone[key].rightStationInZone.userProfile;

      const leftUserId = ('uid' in left) ? [left.uid] : [];
      const rightUserId = ('uid' in right) ? [right.uid] : [];

      return leftUserId.concat(rightUserId);
    });

    return userIds.filter((uid: string, indexOfThisElement: number) => {
      const indexOfFirstOccuranceInArray = userIds.indexOf(uid);
      return indexOfFirstOccuranceInArray === indexOfThisElement;
    });
  }

  private allEventsServerUrl = this.getLiveMetricsPath('all-events') as string;

  constructor(
    // TODO: #2426 the gym object (not name) needs to be passed in, since it is running on the zone screens
    private gymName: string,
    private config: IScheduleBuilderConfig,
    private zoneFallback: ZoneFallback,
    private logger: IClientSideLogger,
    private zoneNumber: number,
  ) {}

  public async getNextSchedule(
    requestTime: Date,
    gymStateForSingleZone?: IGymStateForSingleZone,
    useNextTimeSlice = true,
  ) {
    const zoneNumberAsString = this.zoneNumber.toString();
    const gym = DbGymRepository.getByName(this.gymName);
    let shouldUseFallbackOnError = false;
    try {
      let nextScheduledItem: ScreenSession;
      if (!gymStateForSingleZone || this.isScheduleToOldToTrust(requestTime, gymStateForSingleZone)) {
        this.logger.warn('schedule is too old or not found at all');
        nextScheduledItem = this.zoneFallback.getFallbackSessionForZone(zoneNumberAsString, requestTime, gym);
      } else if (this.zoneNumber) {
        shouldUseFallbackOnError = true;
        nextScheduledItem = this.getNextScheduledCheckedInSession(
          requestTime,
          gymStateForSingleZone,
          this.zoneNumber,
          useNextTimeSlice,
        );
      } else {
        nextScheduledItem = this.zoneFallback.getFallbackSessionForZone(zoneNumberAsString, requestTime, gym);
      }

      return nextScheduledItem;
    } catch (e) {
      if (!shouldUseFallbackOnError) {
        throw e;
      }
      this.logger.error(e as Error, `failed to load next schedule for zone:${this.zoneNumber}`);
      return this.zoneFallback.getFallbackSessionForZone(zoneNumberAsString, requestTime, gym);
    } finally {
      this.logger.trace(`Done loading ${useNextTimeSlice ? 'next' : 'current'} schedule for ${this.zoneNumber}`);
    }
  }

  private isScheduleToOldToTrust(requestTime: Date, gymStateForSingleZone: IGymStateForSingleZone) {
    const firstItem = Object.keys(gymStateForSingleZone)[0];
    const firstSessionTimeWithBackPressure = new Date(firstItem).getTime() + this.config.backPressureWindow;
    const timeDifference = (requestTime.getTime() - firstSessionTimeWithBackPressure) / 1000;
    this.logger.trace(`difference between schedule and now is ${timeDifference} seconds`);
    return timeDifference > this.config.secondsDifferenceAllowedInSchedule;
  }

  private getNextScheduledCheckedInSession(
    requestTime: Date,
    gymStateForSingleZone: IGymStateForSingleZone,
    fallbackZoneId = 1,
    useNextTimeSlice = true,
  ) {
    try {
      const nextTimeSlice = useNextTimeSlice
        ? new TimeSlices(requestTime).nextSliceClosestToTime(requestTime)
        : new TimeSlices(requestTime).previousSliceClosestToTime(requestTime);
      const currentGymState = gymStateForSingleZone[nextTimeSlice.toISOString()];

      if (!currentGymState) {
        this.logger.warn('no gymstate found, returning default');
        return this.getDefaultSessionForToday(fallbackZoneId);
      }

      const {
        leftStationInZone: left,
        rightStationInZone: right,
      } = currentGymState;

      let leftHeartRateMonitorIdentifier;
      if (left.userProfile != null) {
        leftHeartRateMonitorIdentifier = this.getLiveMetricsPath(left.userProfile.uid);
      }
      let rightHeartRateMonitorIdentifier;
      if (right.userProfile != null) {
        rightHeartRateMonitorIdentifier = this.getLiveMetricsPath(right.userProfile.uid);
      }

      return new ScreenSession(
        this.gymName,
        currentGymState.leftStationInZone,
        currentGymState.rightStationInZone,
        currentGymState.audioSequenceForAllZones,
        new Date(currentGymState.startSlice).getTime(),
        new Date(currentGymState.endSlice).getTime(),
        this.allEventsServerUrl,
        leftHeartRateMonitorIdentifier,
        rightHeartRateMonitorIdentifier,
      );
    } catch (e) {
      this.logger.error(e as Error, 'failed to read local schedule: ');
      return this.getDefaultSessionForToday(fallbackZoneId);
    }
  }

  private getDefaultSessionForToday(zoneNumber: number) {
    this.logger.trace(`loading default session for zone ${zoneNumber}`);
    const now = Date.now();
    const defaultTimeout = now + 10000;
    const gym = DbGymRepository.getByName(this.gymName);
    const screenSession = new ScreenSession(
      this.gymName,
      ZoneFallback.createOfflineSession(0, CardioOrResistance.Cardio, gym),
      ZoneFallback.createOfflineSession(1, CardioOrResistance.Resistance, gym),
      [],
      now,
      defaultTimeout,
      this.allEventsServerUrl,
    );
    return screenSession;
  }

  private getLiveMetricsPath(uid: string) {
    return `${this.config.realtimeMetricsServer}/${uid}`;
  }
}
